import { format } from 'date-fns';
import {
  computeSessionsForPrivateLesson,
  generateLessonOccurrences,
  type LessonOccurrence,
  type LessonOverride,
} from '@court-sync/shared';
import type { ScheduleResponse } from '../services/schedule';
import { courtNamesLabelFromLesson } from './courtDisplay';
import type { ScheduleOccurrence } from './scheduleOccurrenceTypes';

export function expandScheduleOccurrences(schedule: ScheduleResponse): ScheduleOccurrence[] {
  const courtNamesById = schedule.courts ?? {};
  const allOccurrences: ScheduleOccurrence[] = [];
  const refundedBookingSet = new Set(schedule.refundedBookingIds ?? []);

  for (const booking of schedule.bookings) {
    const lesson = schedule.lessons[booking.lessonId];
    const academySettings = schedule.academySettings[booking.academyId];

    if (!lesson) continue;

    const lessonOverrides = schedule.overrides
      .filter((o) => o.lessonId === lesson.id)
      .map((o) => ({
        ...o,
        originalDate: new Date(o.originalDate),
        newDate: o.newDate ? new Date(o.newDate) : undefined,
        createdBy: '',
        createdAt: new Date(),
        updatedAt: new Date(),
      })) as LessonOverride[];

    if (lesson.lessonType === 'private' && !booking.termId) {
      const fromApi = schedule.privateLessonSessions?.[lesson.id];
      let slots: Array<{ sessionDate: string; startTime: string; endTime: string }> = [];
      if (fromApi && fromApi.length > 0) {
        slots = fromApi.map((r) => ({
          sessionDate: r.sessionDate,
          startTime: r.startTime,
          endTime: r.endTime,
        }));
      } else if (
        lesson.startDate != null &&
        lesson.numberOfSessions != null &&
        lesson.numberOfSessions > 0
      ) {
        slots = computeSessionsForPrivateLesson(
          {
            id: lesson.id,
            academyId: lesson.academyId,
            startTime: lesson.startTime,
            endTime: lesson.endTime,
          },
          lesson.startDate,
          lesson.numberOfSessions,
          lesson.daysOfWeek
        ).map((row) => ({
          sessionDate: row.sessionDate,
          startTime: row.startTime,
          endTime: row.endTime,
        }));
      }

      if (slots.length === 0) continue;

      const studentName = schedule.students?.[booking.studentId];
      const isRefunded = refundedBookingSet.has(booking.id);
      const termName = 'Private lesson';

      for (const slot of slots) {
        const occDate = new Date(`${slot.sessionDate}T12:00:00`);
        const override = lessonOverrides.find(
          (o) => format(o.originalDate, 'yyyy-MM-dd') === slot.sessionDate
        );
        let status: LessonOccurrence['status'] = 'scheduled';
        if (override) {
          status = override.overrideType === 'cancelled' ? 'cancelled' : 'postponed';
        }
        const occ: LessonOccurrence = {
          date: occDate,
          dayOfWeek: occDate.getDay(),
          lessonId: lesson.id,
          startTime: slot.startTime,
          endTime: slot.endTime,
          status,
          override,
        };
        allOccurrences.push({
          ...occ,
          bookingId: booking.id,
          studentId: booking.studentId,
          sessionId: booking.sessionId ?? undefined,
          studentName,
          lessonName: lesson.name,
          lessonType: lesson.lessonType,
          skillLevel: lesson.skillLevel,
          ballColor: lesson.ballColor,
          ageGroupId: lesson.ageGroupId,
          academyId: booking.academyId,
          makeupLessonEnabled: academySettings?.makeupLessonEnabled ?? false,
          makeupNotificationDeadlineHours:
            academySettings?.makeupNotificationDeadlineHours ?? 24,
          termName,
          isRefunded: isRefunded || undefined,
          courtLabel: courtNamesLabelFromLesson(lesson, courtNamesById),
          bookingScope: 'session' as const,
        });
      }
      continue;
    }

    const sessionDate = booking.sessionDate;
    const sessionYmd =
      typeof sessionDate === 'string' ? sessionDate.split('T')[0]?.slice(0, 10) : undefined;

    // Camp daily bookings: no term — use camp date range or a single booked day
    if (lesson.lessonType === 'camp') {
      if (!lesson.daysOfWeek?.length) continue;
      const campOverrides = schedule.overrides
        .filter((o) => o.lessonId === lesson.id)
        .map((o) => ({
          ...o,
          originalDate: new Date(o.originalDate),
          newDate: o.newDate ? new Date(o.newDate) : undefined,
          createdBy: '',
          createdAt: new Date(),
          updatedAt: new Date(),
        })) as LessonOverride[];

      const dateRange =
        lesson.startDate && lesson.endDate
          ? { startDate: new Date(lesson.startDate), endDate: new Date(lesson.endDate) }
          : null;

      const campOccs = dateRange
        ? generateLessonOccurrences(
            {
              id: lesson.id,
              daysOfWeek: lesson.daysOfWeek,
              startTime: lesson.startTime,
              endTime: lesson.endTime,
            },
            dateRange,
            campOverrides
          )
        : [];

      const occsToAdd = sessionYmd
        ? campOccs.filter((occ) => format(occ.date, 'yyyy-MM-dd') === sessionYmd)
        : campOccs;

      const finalCampOccs =
        sessionYmd && occsToAdd.length === 0
          ? [
              {
                date: new Date(`${sessionYmd}T12:00:00`),
                dayOfWeek: new Date(`${sessionYmd}T12:00:00`).getDay(),
                lessonId: lesson.id,
                startTime: lesson.startTime,
                endTime: lesson.endTime,
                status: 'scheduled' as const,
              },
            ]
          : occsToAdd;

      const studentName = schedule.students?.[booking.studentId];
      const isRefunded = refundedBookingSet.has(booking.id);
      const campLabel = sessionYmd ? 'Camp day' : 'Full camp';

      for (const occ of finalCampOccs) {
        allOccurrences.push({
          ...occ,
          bookingId: booking.id,
          studentId: booking.studentId,
          sessionId: booking.sessionId ?? undefined,
          studentName,
          lessonName: lesson.name,
          lessonType: lesson.lessonType,
          skillLevel: lesson.skillLevel,
          ballColor: lesson.ballColor,
          ageGroupId: lesson.ageGroupId,
          academyId: booking.academyId,
          makeupLessonEnabled: academySettings?.makeupLessonEnabled ?? false,
          makeupNotificationDeadlineHours:
            academySettings?.makeupNotificationDeadlineHours ?? 24,
          termName: campLabel,
          isRefunded: isRefunded || undefined,
          courtLabel: courtNamesLabelFromLesson(lesson, courtNamesById),
          bookingScope: sessionYmd ? ('session' as const) : ('term' as const),
        });
      }
      continue;
    }

    if (!lesson.daysOfWeek?.length) continue;

    const term = booking.termId ? schedule.terms[booking.termId] : undefined;
    if (!term) continue;

    const occs = generateLessonOccurrences(
      {
        id: lesson.id,
        daysOfWeek: lesson.daysOfWeek,
        startTime: lesson.startTime,
        endTime: lesson.endTime,
      },
      {
        startDate: new Date(term.startDate),
        endDate: new Date(term.endDate),
      },
      lessonOverrides
    );

    const bookingScope: 'term' | 'session' = sessionYmd ? 'session' : 'term';
    let occsToAdd = sessionYmd
      ? occs.filter((occ) => format(occ.date, 'yyyy-MM-dd') === sessionYmd)
      : occs;

    // Drop-in session (or camp day) when the date is not in the generated series — still show on calendar
    if (sessionYmd && occsToAdd.length === 0) {
      const dropInDate = new Date(`${sessionYmd}T12:00:00`);
      occsToAdd = [
        {
          date: dropInDate,
          dayOfWeek: dropInDate.getDay(),
          lessonId: lesson.id,
          startTime: lesson.startTime,
          endTime: lesson.endTime,
          status: 'scheduled' as const,
        },
      ];
    }

    const termDateRange = `${format(new Date(term.startDate), 'MMM d')} – ${format(new Date(term.endDate), 'MMM d, yyyy')}`;
    const termName = term.name;
    const studentName = schedule.students?.[booking.studentId];
    const isRefunded = refundedBookingSet.has(booking.id);

    for (const occ of occsToAdd) {
      allOccurrences.push({
        ...occ,
        bookingId: booking.id,
        studentId: booking.studentId,
        sessionId: booking.sessionId ?? undefined,
        studentName,
        lessonName: lesson.name,
        lessonType: lesson.lessonType,
        skillLevel: lesson.skillLevel,
        ballColor: lesson.ballColor,
        ageGroupId: lesson.ageGroupId,
        academyId: booking.academyId,
        makeupLessonEnabled: academySettings?.makeupLessonEnabled ?? false,
        makeupNotificationDeadlineHours:
          academySettings?.makeupNotificationDeadlineHours ?? 24,
        termName,
        termDateRange,
        isRefunded: isRefunded || undefined,
        courtLabel: courtNamesLabelFromLesson(lesson, courtNamesById),
        bookingScope,
      });
    }
  }

  for (const res of schedule.makeupSessionReservations ?? []) {
    const lesson = schedule.lessons[res.lessonId];
    const term = schedule.terms[res.termId];
    const academySettings = schedule.academySettings[res.academyId];

    if (!lesson || !term || !lesson.daysOfWeek?.length) continue;

    const lessonOverrides = schedule.overrides
      .filter((o) => o.lessonId === lesson.id)
      .map((o) => ({
        ...o,
        originalDate: new Date(o.originalDate),
        newDate: o.newDate ? new Date(o.newDate) : undefined,
        createdBy: '',
        createdAt: new Date(),
        updatedAt: new Date(),
      })) as LessonOverride[];

    const occs = generateLessonOccurrences(
      {
        id: lesson.id,
        daysOfWeek: lesson.daysOfWeek,
        startTime: lesson.startTime,
        endTime: lesson.endTime,
      },
      {
        startDate: new Date(term.startDate),
        endDate: new Date(term.endDate),
      },
      lessonOverrides
    );

    const sessionDateYmd = res.sessionDate.split('T')[0];
    const occsToAdd = occs.filter((occ) => format(occ.date, 'yyyy-MM-dd') === sessionDateYmd);

    const termDateRange = `${format(new Date(term.startDate), 'MMM d')} – ${format(new Date(term.endDate), 'MMM d, yyyy')}`;
    const studentName = schedule.students?.[res.studentId];

    for (const occ of occsToAdd) {
      allOccurrences.push({
        ...occ,
        bookingId: res.id,
        studentId: res.studentId,
        studentName,
        lessonName: lesson.name,
        lessonType: lesson.lessonType,
        skillLevel: lesson.skillLevel,
        ballColor: lesson.ballColor,
        ageGroupId: lesson.ageGroupId,
        academyId: res.academyId,
        makeupLessonEnabled: academySettings?.makeupLessonEnabled ?? false,
        makeupNotificationDeadlineHours:
          academySettings?.makeupNotificationDeadlineHours ?? 24,
        termName: term.name,
        termDateRange,
        courtLabel: courtNamesLabelFromLesson(lesson, courtNamesById),
      });
    }
  }

  const ymdKey = (s: string) => s.replace(/T.*/, '').slice(0, 10);
  const hiddenOccurrenceSet = new Set([
    ...(schedule.reportedAbsenceOccurrences ?? []).map((o) => `${o.bookingId}:${ymdKey(o.date)}`),
    ...(schedule.excludedSessionOccurrences ?? []).map((o) => `${o.bookingId}:${ymdKey(o.date)}`),
  ]);
  const filtered = allOccurrences.filter(
    (occ) => !hiddenOccurrenceSet.has(`${occ.bookingId}:${format(occ.date, 'yyyy-MM-dd')}`)
  );

  filtered.sort((a, b) => a.date.getTime() - b.date.getTime());
  return filtered;
}
